import { CardNumber, Cvv, ExpiryDate, Frames, FramesStyle } from 'frames-react';
import { CheckoutConfig } from '@share/interfaces';
import { ThemeType } from '@share/enums';
import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { user } from '@state/user';
import CreditCardIcon from '@img/visa-master.webp';
import SkeletonImage from '@com/utils/SkeletonImage';
import Spinner from '@com/utils/Spinner';
import styled from '@emotion/styled';

interface Props {
  isPaying: boolean;
  errorMsg?: string;
  config?: CheckoutConfig;
  handleSubmit: () => void;
}

const ErrorMsg = styled.div`
  color: #ee6666;
  margin-top: 0.55em;
  font-size: 1.4em;
`;

const Button = styled.button`
  :hover,
  :focus {
    color: #fff;
    opacity: 0.9;
  }
`;

function CheckoutPaymentFrame({
  isPaying,
  errorMsg,
  config,
  handleSubmit,
}: Props): JSX.Element {
  /* Define state / hook */
  const [isLibLoaded, setIsLibLoaded] = useState(false);
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  const { i18n, t } = useTranslation();
  const { theme } = user.useContainer();

  /* Custom Data Object for render or other usages */

  /* Render control function / logic */
  const getFrameStyle = (): FramesStyle => {
    const baseColor = theme === ThemeType.LIGHT ? '#14141F' : '#fff';
    return {
      base: {
        borderRadius: '8px',
        border: '1px solid rgba(138, 138, 160, 0.3)',
        fontSize: '14px',
        padding: '9px 20px',
        outline: '0',
        height: '48px',
        color: baseColor,
      },
      placeholder: {
        base: {
          color: baseColor,
        },
      },
    };
  };

  /* Data Change Listeners - useEffect/useMemo/useCallback */
  useEffect(() => {
    // https://www.checkout.com/docs/testing/test-card-numbers
    // Make sure only one library is loaded
    const scriptSrc = 'https://cdn.checkout.com/js/framesv2.min.js';
    const existingScriptTag = document.querySelector(
      `script[src='${scriptSrc}']`
    );
    const initLib = () => {
      const scriptTag = document.createElement('script');
      scriptTag.src = scriptSrc;
      scriptTag.addEventListener('load', () => setIsLibLoaded(true));
      document.head.appendChild(scriptTag);
    };
    if (existingScriptTag) document.head.removeChild(existingScriptTag);
    if (config?.PUBLIC_KEY) initLib();
  }, [config]);

  /* Styling */

  /* Render */
  return (
    <>
      <div className="mb-3 d-flex ml-n2">
        <SkeletonImage height={50} src={CreditCardIcon} />
      </div>
      {isLibLoaded && config?.PUBLIC_KEY && (
        <Frames
          key={`${i18n.language}-${theme}`}
          config={{
            publicKey: config?.PUBLIC_KEY,
            style: getFrameStyle(),
            localization: {
              cardNumberPlaceholder: t('modal.cardNumber'),
              expiryMonthPlaceholder: t('modal.mm'),
              expiryYearPlaceholder: t('modal.yy'),
              cvvPlaceholder: t('modal.cvv'),
            },
          }}
          ready={() => {
            setIsFrameLoaded(true);
          }}
        >
          {!isFrameLoaded && (
            <Spinner className="mx-auto my-4" height={48} width={48} />
          )}
          <div
            css={css`
              padding: 0 1rem;
              display: ${isFrameLoaded ? 'block' : 'none'};
            `}
          >
            <div className="row">
              <CardNumber
                css={css`
                  width: 100%;
                  height: 50px;
                `}
              />
            </div>
            <div className="row">
              <div className="col-lg-6 col-sm-12 p-0 mt-4">
                <ExpiryDate
                  css={css`
                    height: 50px;
                    margin-right: 16px;

                    @media (max-width: 992px) {
                      margin-right: 0px;
                    }
                  `}
                />
              </div>
              <div className="col-lg-6 col-sm-12 p-0 mt-4">
                <Cvv
                  css={css`
                    height: 50px;
                  `}
                />
              </div>
            </div>
          </div>

          {errorMsg && <ErrorMsg>{errorMsg}</ErrorMsg>}
          <div className="row mt-5 d-flex justify-content-center">
            <Button
              className="align-items-center m-auto"
              css={css`
                display: ${isFrameLoaded ? 'flex' : 'none'};
              `}
              data-test="pay-btn"
              type="button"
              onClick={() => {
                handleSubmit();
              }}
            >
              {t('modal.payNow')}
              <Spinner
                className="ml-2"
                color="#fff"
                width={isPaying ? 24 : 0}
              />
            </Button>
          </div>
        </Frames>
      )}
    </>
  );
}

export default CheckoutPaymentFrame;
